import { useEffect, useState } from 'react';
import { saveThemePreference } from '../../api/preferences';
import { StatusBanner } from '../../components/ui';
import { panelClassName } from '../ui/classes';
import { ADMIN_THEMES, applyTheme, normalizeTheme } from '../theme/themes';

function ThemeSettings({ user, token, onUserUpdate, embedded = false }) {
  const [selectedTheme, setSelectedTheme] = useState(normalizeTheme(user?.theme));
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [savingTheme, setSavingTheme] = useState('');

  useEffect(() => {
    const theme = normalizeTheme(user?.theme);
    setSelectedTheme(theme);
    applyTheme(theme);
  }, [user?.theme]);

  const handleSelectTheme = async (themeId) => {
    if (themeId === selectedTheme && status.type === 'success') {
      return;
    }

    setSavingTheme(themeId);
    setStatus({ type: 'idle', message: '' });
    applyTheme(themeId);

    try {
      const payload = await saveThemePreference(token, themeId);
      setSelectedTheme(themeId);
      onUserUpdate(payload.user);
      setStatus({ type: 'success', message: 'Theme saved to your account.' });
    } catch (error) {
      const fallback = normalizeTheme(user?.theme);
      applyTheme(fallback);
      setSelectedTheme(fallback);
      setStatus({
        type: 'error',
        message: error.message || 'Unable to save theme preference.',
      });
    } finally {
      setSavingTheme('');
    }
  };

  const content = (
    <>
      {!embedded ? (
        <div className="border-b border-[rgb(var(--crm-border))] px-5 py-4">
          <h2 className="text-base font-semibold text-[rgb(var(--crm-text))]">Appearance</h2>
          <p className="mt-1 text-sm text-[rgb(var(--crm-text-muted))]">
            Choose a workspace theme. Your selection is saved to your account.
          </p>
        </div>
      ) : null}

      <div className={`grid gap-3 ${embedded ? '' : 'px-5 py-5'} sm:grid-cols-2 xl:grid-cols-3`}>
        {ADMIN_THEMES.map((theme) => {
          const isActive = selectedTheme === theme.id;
          const isSaving = savingTheme === theme.id;

          return (
            <button
              key={theme.id}
              type="button"
              className={[
                'grid gap-3 rounded-[10px] border p-4 text-left transition',
                isActive
                  ? 'border-[rgb(var(--crm-accent))] ring-2 ring-[rgb(var(--crm-accent)/0.15)]'
                  : 'border-[rgb(var(--crm-border))] hover:border-[rgb(var(--crm-accent)/0.45)]',
                isSaving ? 'opacity-70' : '',
              ].join(' ')}
              onClick={() => handleSelectTheme(theme.id)}
              disabled={!!savingTheme}
              aria-pressed={isActive}
            >
              <div className="flex gap-2">
                {theme.preview.map((color) => (
                  <span
                    key={color}
                    className="h-8 flex-1 rounded-md border border-black/5"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div>
                <strong className="block text-sm text-[rgb(var(--crm-text))]">{theme.label}</strong>
                <span className="mt-1 block text-xs leading-relaxed text-[rgb(var(--crm-text-muted))]">
                  {theme.description}
                </span>
              </div>
              <span className="text-xs font-semibold text-[rgb(var(--crm-accent-text))]">
                {isSaving ? 'Saving...' : isActive ? 'Active' : 'Use theme'}
              </span>
            </button>
          );
        })}
      </div>

      <StatusBanner status={status} setStatus={setStatus} className={embedded ? 'mt-4' : 'mx-5 mb-5'} />
    </>
  );

  if (embedded) {
    return content;
  }

  return <div className={panelClassName}>{content}</div>;
}

export default ThemeSettings;
